import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle, Paperclip, MessageCircle, Plus } from 'lucide-react';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { getAllWorkflowTasks, createWorkflowTask, moveWorkflowTask, getAllEngagements } from '../../store/actions';
import { PRIORITY, TASK_TYPE, WAITING_ON, WORKFLOW_STAGE, formatDate, initials, label, options } from '../../constants/garmentCodes';

const COLUMNS = options(WORKFLOW_STAGE);
const WAITING = { BRAND: 1, MANUFACTURER: 2 };
const PRIORITY_DOT = { 1: 'bg-[#C0222B]', 2: 'bg-[#92560C]', 3: 'bg-[#9B9893]' };
const TYPE_BORDER = {
  1: 'border-l-[#1A6B3E]',
  3: 'border-l-[#0C6E6E]',
  4: 'border-l-[#3D52A0]',
  6: 'border-l-[#5B21B6]',
  8: 'border-l-[#92560C]',
  9: 'border-l-[#C0222B]',
  11: 'border-l-[#1A6B3E]',
};
const inputCls = 'w-full text-sm border border-[#E5E3DE] rounded-lg px-3 py-2 outline-none focus:border-[#3D52A0]';
const EMPTY_FORM = { engagementId: '', title: '', type: '', boardColumn: '1', priority: '2', waitingOn: '2', dueDate: '' };

function KanbanCard({ card, dimmed }) {
  return (
    <div
      draggable={!card.__optimistic}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(card.id))}
      className={`bg-white rounded-lg border border-[#E5E3DE] border-l-4 ${TYPE_BORDER[card.type] || 'border-l-[#9B9893]'} p-3 shadow-sm hover:shadow-md transition-all cursor-grab ${dimmed ? 'opacity-30' : ''} ${card.__optimistic ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          {card.overdue && <AlertTriangle size={10} className="text-[#C0222B]" />}
          {card.priority && <div className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[card.priority]}`} title={label(PRIORITY, card.priority)} />}
          {card.waitingOn && (
            <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${card.waitingOn === WAITING.BRAND ? 'bg-[#FDECEA] text-[#C0222B]' : 'bg-[#F4F3F0] text-[#9B9893]'}`}>
              {card.waitingOn === WAITING.BRAND ? 'Action needed' : 'Mfr'}
            </span>
          )}
        </div>
        <span className="text-[10px] font-mono text-[#9B9893]">{card.linkedItemType ? `${card.linkedItemType} #${card.linkedItemId}` : label(TASK_TYPE, card.type, '')}</span>
      </div>
      <p className="text-xs font-semibold text-[#1A1915] leading-tight">{card.title}</p>
      <div className="flex items-center justify-between mt-2.5">
        <div className="flex items-center gap-2 text-[#9B9893]">
          {card.attachments > 0 && <span className="flex items-center gap-0.5 text-[10px]"><Paperclip size={9} />{card.attachments}</span>}
          {card.comments > 0 && <span className="flex items-center gap-0.5 text-[10px]"><MessageCircle size={9} />{card.comments}</span>}
        </div>
        {card.dueDate && <span className={`text-[10px] font-medium ${card.overdue ? 'text-[#C0222B]' : 'text-[#92560C]'}`}>{formatDate(card.dueDate)}</span>}
      </div>
      {card.assignee && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-[#F4F3F0]">
          <div className="w-4 h-4 rounded-full bg-[#1E2A3B] flex items-center justify-center"><span className="text-[7px] font-bold text-white">{initials(card.assignee)}</span></div>
          <span className="text-[10px] text-[#9B9893] truncate">{card.assignee}</span>
        </div>
      )}
    </div>
  );
}

export default function BrandBoard() {
  const dispatch = useDispatch();
  const tasks = useSelector((state) => state.workflowtask.tasks);
  const loading = useSelector((state) => state.workflowtask.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brandId = useSelector((state) => state.brand.profile?.id);
  const [engagementId, setEngagementId] = useState('');
  const [highlightWaiting, setHighlightWaiting] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const scrollRef = useRef(null);
  const [drag, setDrag] = useState({ down: false, startX: 0, scrollLeft: 0 });

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllWorkflowTasks({ brandId, engagementId }));
  }, [dispatch, brandId, engagementId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const byColumn = useMemo(
    () =>
      tasks.reduce((acc, t) => {
        (acc[t.boardColumn] = acc[t.boardColumn] || []).push(t);
        return acc;
      }, {}),
    [tasks],
  );

  const handleMouseDown = (e) => {
    if (e.target.closest('button, input, a, select, textarea, [draggable="true"]')) return;
    setDrag({ down: true, startX: e.pageX - (scrollRef.current?.offsetLeft || 0), scrollLeft: scrollRef.current?.scrollLeft || 0 });
  };
  const handleMouseUp = () => setDrag((d) => ({ ...d, down: false }));
  const handleMouseMove = (e) => {
    if (!drag.down || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = drag.scrollLeft - (x - drag.startX) * 1.5;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' ? null : Number(v));
  const create = (e) => {
    e.preventDefault();
    dispatch(
      createWorkflowTask({
        payload: {
          engagementId: Number(form.engagementId),
          title: form.title.trim(),
          type: num(form.type),
          boardColumn: num(form.boardColumn),
          priority: num(form.priority),
          waitingOn: num(form.waitingOn),
          dueDate: form.dueDate || null,
          comments: 0,
          attachments: 0,
          overdue: false,
        },
      }),
    );
    setForm(EMPTY_FORM);
    setAddOpen(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Task Board"
        subtitle="Drag cards between stages"
        actions={
          <div className="flex items-center gap-2">
            <select value={engagementId} onChange={(e) => setEngagementId(e.target.value)} className="text-xs border border-[#E5E3DE] rounded-lg px-2 py-1.5 bg-white outline-none">
              <option value="">All engagements</option>
              {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <div className="flex rounded-lg border border-[#E5E3DE] overflow-hidden text-xs">
              {[{ key: null, text: 'All' }, { key: WAITING.BRAND, text: 'Action needed' }, { key: WAITING.MANUFACTURER, text: 'Waiting on Mfr' }].map((f) => (
                <button key={String(f.key)} onClick={() => setHighlightWaiting(f.key)} className={`px-3 py-1.5 transition-colors ${highlightWaiting === f.key ? 'bg-[#1E2A3B] text-white font-medium' : 'bg-white text-[#5C5A54] hover:bg-[#F4F3F0]'}`}>
                  {f.text}
                </button>
              ))}
            </div>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>Add Task</Button>
          </div>
        }
      />

      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        className={`flex-1 overflow-auto kanban-scroll p-4 pb-16 ${drag.down ? 'cursor-grabbing' : ''}`}
      >
        {loading && <p className="text-xs text-[#9B9893] mb-2">Loading…</p>}
        <div className="kanban-board flex gap-3.5 min-w-max pb-8 pr-12 items-start">
          {COLUMNS.map((col) => {
            const cards = byColumn[col.value] || [];
            return (
              <div
                key={col.value}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData('text/plain');
                  if (id) dispatch(moveWorkflowTask({ id, boardColumn: col.value }));
                }}
                className="flex-shrink-0 w-60 flex flex-col gap-2 bg-[#F4F3F0] rounded-xl p-3 pb-4"
              >
                <div className="flex items-center justify-between px-1 mb-1">
                  <h3 className="text-[11px] font-bold text-[#5C5A54] uppercase tracking-wider">{col.label}</h3>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cards.length > 0 ? 'bg-[#1E2A3B] text-white' : 'bg-[#E5E3DE] text-[#9B9893]'}`}>{cards.length}</span>
                </div>
                {cards.map((card) => <KanbanCard key={card.id} card={card} dimmed={highlightWaiting != null && card.waitingOn !== highlightWaiting} />)}
                {cards.length === 0 && <div className="flex-1 flex items-center justify-center py-6"><p className="text-[10px] text-[#C9C7C2] text-center">No cards</p></div>}
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Create Workflow Task" size="md">
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Engagement *</label>
            <select required value={form.engagementId} onChange={set('engagementId')} className={inputCls}>
              <option value="">Select engagement</option>
              {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Task Title *</label>
            <input required value={form.title} onChange={set('title')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Stage</label>
              <select value={form.boardColumn} onChange={set('boardColumn')} className={inputCls}>
                {COLUMNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Type</label>
              <select value={form.type} onChange={set('type')} className={inputCls}>
                <option value="">—</option>
                {options(TASK_TYPE).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Priority</label>
              <select value={form.priority} onChange={set('priority')} className={inputCls}>
                {options(PRIORITY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Waiting On</label>
              <select value={form.waitingOn} onChange={set('waitingOn')} className={inputCls}>
                {options(WAITING_ON).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#5C5A54] mb-1">Due Date</label>
            <input type="date" value={form.dueDate} onChange={set('dueDate')} className={inputCls} />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm">Create Task</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
